/* №4 «Харилцагч» — server жагсаалт + ирэлтийн түүх (дизайны customers.jsx-ээс) */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cwApi, type CwCustomerDto } from '@/api/carwash';
import { CAR_TYPES, carTypeLabel } from '@/shared/data';
import { tug } from '@/shared/format';
import {
  IconCar, IconChevronLeft, IconChevronRight, IconPhone, IconSearch, IconX,
} from '@/shared/icons';
import { useShellUi } from '@/features/shell/ShellLayout';

function PlateTag({ plate, sm }: { plate: string; sm?: boolean }) {
  return (
    <span className={'inline-flex items-center border-slate-800 bg-white text-slate-900 font-extrabold tracking-wider leading-none rounded-[7px] whitespace-nowrap ' +
      (sm ? 'text-[14px] px-2 py-[3px] border-[1.5px]' : 'text-[18px] px-2.5 py-[5px] border-2')}>{plate}</span>
  );
}

function lastVisitText(c: CwCustomerDto): string {
  if (!c.LastVisitAt) return '—';
  const d = new Date(c.LastVisitAt.endsWith('Z') ? c.LastVisitAt : c.LastVisitAt + 'Z');
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function paidDateText(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function CustomerDetail({ cust }: { cust: CwCustomerDto }) {
  const type = carTypeLabel(cust.CarType);
  const TypeIcon = CAR_TYPES[type] || IconCar;
  const name = cust.Name || 'Харилцагч';
  const historyQ = useQuery({
    queryKey: ['cw', 'customer-orders', cust.Id],
    queryFn: () => cwApi.customerOrders(cust.Id),
  });
  return (
    <div className="flex flex-col gap-4">
      {/* толгой */}
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--brand1)] to-[var(--brand2)] text-white flex items-center justify-center text-[18px] font-bold shrink-0">
          {name[0]}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[18px] font-extrabold text-slate-900 leading-tight">{name}</div>
          <div className="flex items-center gap-1.5 text-[13px] text-slate-500 mt-0.5">
            <IconPhone size={13} /> {cust.Phone || '—'}
          </div>
        </div>
        <PlateTag plate={cust.Plate} />
      </div>

      {/* мета */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-[var(--r)] bg-slate-50 ring-1 ring-slate-100 p-3">
          <div className="flex items-center gap-1.5 text-[12px] text-slate-500"><TypeIcon size={14} className="text-slate-400" />Төрөл</div>
          <div className="text-[15px] font-bold text-slate-900 mt-1">{type}</div>
        </div>
        <div className="rounded-[var(--r)] bg-slate-50 ring-1 ring-slate-100 p-3">
          <div className="text-[12px] text-slate-500">Нийт ирэлт</div>
          <div className="text-[15px] font-bold text-slate-900 mt-1">{cust.Visits} удаа</div>
        </div>
        <div className="rounded-[var(--r)] bg-slate-50 ring-1 ring-slate-100 p-3">
          <div className="text-[12px] text-slate-500">Нийт төлсөн</div>
          <div className="text-[15px] font-bold text-slate-900 mt-1">{tug(cust.TotalSpent)}</div>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-[var(--r)] bg-slate-50 ring-1 ring-slate-100 px-3.5 py-3">
        <span className="text-[13px] text-slate-500">Сүүлийн ирэлт</span>
        <span className="text-[13px] font-bold text-slate-900">{lastVisitText(cust)}</span>
      </div>

      {/* Ирэлтийн түүх — хэзээ, ямар үйлчилгээ, хэдээр */}
      <div className="flex flex-col gap-2">
        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Ирэлтийн түүх</div>
        <div className="flex flex-col rounded-[var(--r)] ring-1 ring-slate-100 overflow-hidden">
          {(historyQ.data ?? []).map((h, i) => (
            <div key={h.Id} className={'flex items-center gap-3 px-3.5 py-3 ' + (i % 2 ? 'bg-slate-50/60' : 'bg-white')}>
              <span className="text-[13px] font-semibold text-slate-400 w-10 shrink-0">{paidDateText(h.PaidAt)}</span>
              <span className="text-[13px] text-slate-700 flex-1 min-w-0 truncate" title={h.Services.join(', ')}>
                {h.Services.join(', ') || '—'}
              </span>
              <span className="text-[13px] font-bold text-slate-900 whitespace-nowrap">{tug(h.Total)}</span>
            </div>
          ))}
          {historyQ.isLoading && (
            <div className="px-3.5 py-4 text-[13px] text-slate-400 text-center">Ачааллаж байна…</div>
          )}
          {!historyQ.isLoading && (historyQ.data ?? []).length === 0 && (
            <div className="px-3.5 py-4 text-[13px] text-slate-400 text-center">Төлбөр хийгдсэн ирэлт алга</div>
          )}
        </div>
      </div>

      {cust.Note && (
        <div className="rounded-[var(--r)] bg-amber-50 ring-1 ring-amber-100 px-3.5 py-3 text-[13px] text-amber-800">
          {cust.Note}
        </div>
      )}
    </div>
  );
}

function CustomerRow({ cust, active, onClick }: { cust: CwCustomerDto; active: boolean; onClick: () => void }) {
  const type = carTypeLabel(cust.CarType);
  const TypeIcon = CAR_TYPES[type] || IconCar;
  const name = cust.Name || 'Харилцагч';
  return (
    <button onClick={onClick} className={
      'w-full flex items-center gap-3 px-3 py-3 rounded-[var(--r)] ring-1 transition text-left ' +
      (active ? 'bg-cyan-50 ring-2 ring-[var(--brand2)]' : 'bg-white ring-slate-200 hover:ring-slate-300')
    }>
      <div className="w-9 h-9 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-[14px] font-bold shrink-0">{name[0]}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-bold text-slate-900 truncate">{name}</span>
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-400"><TypeIcon size={12} />{type}</span>
        </div>
        <div className="text-[12px] text-slate-500 mt-0.5">{cust.Phone || '—'} · {cust.Visits} ирэлт</div>
      </div>
      <PlateTag plate={cust.Plate} sm />
      <IconChevronRight size={16} className="text-slate-300 shrink-0" />
    </button>
  );
}

export function CustomersPage() {
  const { isMobile: mobile } = useShellUi();
  const [query, setQuery] = useState('');
  const [selPlate, setSelPlate] = useState<string | null>(null);

  const listQ = useQuery({ queryKey: ['cw', 'customers'], queryFn: cwApi.customers });
  const all = listQ.data ?? [];

  const filtered = all.filter((c) => {
    const s = query.trim().toLowerCase();
    if (!s) return true;
    return (c.Plate + ' ' + (c.Phone || '') + ' ' + (c.Name || '')).toLowerCase().includes(s);
  });
  const selected = (selPlate != null ? filtered.find((c) => c.Plate === selPlate) : undefined)
    ?? (mobile ? undefined : filtered[0]);

  const searchBox = (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"><IconSearch size={17} /></span>
      <input value={query} onChange={(e) => { setQuery(e.target.value); setSelPlate(null); }}
        placeholder="Улсын дугаар эсвэл утсаар хайх…"
        className="w-full h-12 pl-10 pr-10 rounded-[var(--r)] bg-white ring-1 ring-slate-200 text-[14px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--brand2)] transition" />
      {query && (
        <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100"><IconX size={15} /></button>
      )}
    </div>
  );

  const emptyText = listQ.isLoading ? 'Ачааллаж байна…' : 'Олдсонгүй';

  if (mobile) {
    if (selected && selPlate) {
      return (
        <div className="h-full flex flex-col">
          <div className="px-4 pt-4 pb-3 border-b border-slate-100 bg-white flex items-center gap-2">
            <button onClick={() => setSelPlate(null)} className="w-9 h-9 -ml-1 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100"><IconChevronLeft size={20} /></button>
            <span className="font-bold text-slate-900 text-[16px]">Харилцагч</span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 pb-24"><CustomerDetail cust={selected} /></div>
        </div>
      );
    }
    return (
      <div className="h-full flex flex-col">
        <div className="px-4 pt-4 pb-3 bg-white border-b border-slate-100 flex flex-col gap-3">
          <span className="font-bold text-slate-900 text-[16px]">Харилцагч</span>
          {searchBox}
        </div>
        <div className="flex-1 overflow-y-auto p-3 pb-24 flex flex-col gap-2">
          {filtered.map((c) => <CustomerRow key={c.Id} cust={c} active={false} onClick={() => setSelPlate(c.Plate)} />)}
          {filtered.length === 0 && <div className="text-center text-slate-400 text-sm py-16">{emptyText}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex min-h-0">
      <div className="w-[380px] shrink-0 border-r border-slate-200 flex flex-col bg-slate-50/50">
        <div className="p-4 pb-3">{searchBox}</div>
        <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-2">
          {filtered.map((c) => <CustomerRow key={c.Id} cust={c} active={!!selected && selected.Id === c.Id} onClick={() => setSelPlate(c.Plate)} />)}
          {filtered.length === 0 && <div className="text-center text-slate-400 text-sm py-16">{emptyText}</div>}
        </div>
      </div>
      <div className="flex-1 min-w-0 overflow-y-auto p-6">
        {selected ? <div className="max-w-[560px]"><CustomerDetail cust={selected} /></div>
          : <div className="h-full flex items-center justify-center text-slate-400">Харилцагч сонгоно уу</div>}
      </div>
    </div>
  );
}
